import {
	useCallback,
	useEffect,
	useReducer,
	useRef,
	useState,
	type ChangeEvent,
} from "react";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import { helpers } from "../../util/helpers.ts";
import { showNotification } from "../../util/showNotification.ts";
import { toWorker } from "../../util/toWorker.ts";
import { useLocal } from "../../util/local.ts";
import { DataTable } from "../../components/DataTable/index.tsx";
import type { View } from "../../../common/types.ts";
import { PHASE, TIME_BETWEEN_GAMES } from "../../../common/constants.ts";
import { groupByUnique, orderBy } from "../../../common/utils.ts";
import { FancySelect, height } from "./FancySelect.tsx";
import { getTeamCols, SummaryTable } from "./SummaryTable.tsx";
import { Dropdown } from "react-bootstrap";
import { RegenerateScheduleModal } from "./RegenerateScheduleModal.tsx";
import clsx from "clsx";
import { useBlocker } from "../../hooks/useBlocker.ts";
import { StickyBottomButtons } from "../../components/StickyBottomButtons.tsx";
import { confirm } from "../../util/confirm.tsx";
import { csvFormatRows, csvParse } from "d3-dsv";
import { downloadFile } from "../../util/downloadFile.ts";
import { resetFileInput } from "../../util/resetFileInput.ts";
import { IMPORT_FILE_STYLE } from "../../components/ImportFileButton.tsx";

type Schedule = View<"scheduleEditor">["schedule"];

// Not All-Star Game or Trade Deadline
type ActualGame = Extract<Schedule[number], { type: "completed" | "game" }>;

type ScheduleDay = {
	day: number;
	gamesByAwayTid: Record<number, ActualGame>;
	gamesByHomeTid: Record<number, ActualGame>;
	special: "allStarGame" | "tradeDeadline" | undefined;
};

type Team = View<"scheduleEditor">["teams"][number];

// If these are ever changed it will break any previous CSV schedules on import, so be careful!
const ALL_STAR_GAME_LABEL = "All-Star Game";
const TRADE_DEADLINE_LABEL = "Trade Deadline";

const deleteSpecial = (
	schedule: Schedule,
	type: "allStarGame" | "tradeDeadline",
) => {
	let day: number | undefined;
	return schedule
		.filter((row) => {
			if (row.type === type) {
				day = row.day;
				return false;
			}
			return true;
		})
		.map((game) => {
			if (day === undefined || game.day <= day) {
				return game;
			}

			return {
				...game,
				day: game.day - 1,
			};
		});
};

const addGameOnDayByItself = (
	schedule: Schedule,
	gameToAdd: Schedule[number],
) => {
	let added = false;
	const scheduleWithNewDay = schedule.flatMap((game) => {
		if (game.day < gameToAdd.day) {
			return game;
		}

		if (!added) {
			added = true;
			return [
				gameToAdd,
				{
					...game,
					day: game.day + 1,
				},
			];
		}

		return {
			...game,
			day: game.day + 1,
		};
	});

	if (!added) {
		added = true;
		scheduleWithNewDay.push(gameToAdd);
	}

	return scheduleWithNewDay;
};

const notifyPlaceSpecial = (
	type: "allStarGame" | "tradeDeadline",
	day: number,
) => {
	// Without setTimeout, React complains about updating another component when rendering
	setTimeout(() => {
		showNotification({
			type: "info",
			text: `Placed the ${type === "allStarGame" ? ALL_STAR_GAME_LABEL : TRADE_DEADLINE_LABEL} on day ${day}`,
		});
	});
};

const findDuplicates = (strings: string[]) => {
	const seen = new Set<string>();
	const duplicates = new Set<string>();

	for (const string of strings) {
		if (seen.has(string)) {
			duplicates.add(string);
		} else {
			seen.add(string);
		}
	}

	return duplicates;
};

const validateAndParseScheduleCSV = (
	csvText: string,
	teams: Team[],
	maxDayAlreadyPlayed: number,
): Schedule => {
	const parsed = csvParse(csvText);

	const teamsByAbbrev = groupByUnique(teams, (t) => t.seasonAttrs.abbrev);

	const teamColumns = parsed.columns.slice(1).map((abbrev) => abbrev.trim());
	if (!teamColumns[0]) {
		throw new Error("No team columns found");
	}
	for (const abbrev of teamColumns) {
		if (!teamsByAbbrev[abbrev]) {
			throw new Error(
				`Unknown abbrev in header: "${abbrev}". Available teams: ${teams.map((t) => t.seasonAttrs.abbrev).join(", ")}`,
			);
		}
	}
	const duplicateAbbrevs = findDuplicates(teamColumns);
	if (duplicateAbbrevs.size > 0) {
		const array = Array.from(duplicateAbbrevs);
		throw new Error(`Duplicate abbrevs in header: ${array.join(", ")}`);
	}

	const gamesByDay = new Map<number, { away: Team; home: Team }[]>();
	const teamsByDay = new Map<number, Set<number>>();
	const specials: Extract<
		Schedule[number],
		{ type: "allStarGame" | "tradeDeadline" }
	>[] = [];

	for (const row of parsed) {
		if (!parsed.columns[0]) {
			continue;
		}
		const dayStr = row[parsed.columns[0]];
		if (!dayStr) {
			continue;
		}

		const day = Number.parseInt(dayStr);
		if (Number.isNaN(day)) {
			throw new Error(`Invalid day "${dayStr}". Must be a number.`);
		}
		if (day <= maxDayAlreadyPlayed) {
			throw new Error(
				`Day ${day} has already been played. Cannot upload schedules for days <= ${maxDayAlreadyPlayed}.`,
			);
		}

		const firstCell = row[teamColumns[0]]?.trim();
		if (
			firstCell === ALL_STAR_GAME_LABEL ||
			firstCell === TRADE_DEADLINE_LABEL
		) {
			for (const abbrev of teamColumns.slice(1)) {
				if (row[abbrev]?.trim()) {
					throw new Error(`${firstCell} must be the only entry on day ${day}`);
				}
			}
			specials.push(
				firstCell === ALL_STAR_GAME_LABEL
					? { type: "allStarGame", day, homeTid: -1, awayTid: -2 }
					: { type: "tradeDeadline", day, homeTid: -3, awayTid: -3 },
			);
			continue;
		}

		const dayGames = gamesByDay.getOrInsertComputed(day, () => []);
		const teamsOnDay = teamsByDay.getOrInsertComputed(day, () => new Set());

		for (const colAbbrev of teamColumns) {
			const cellAbbrev = row[colAbbrev]?.trim();
			if (!cellAbbrev) {
				continue;
			}

			const colTeam = teamsByAbbrev[colAbbrev]!;

			const cellTeam = teamsByAbbrev[cellAbbrev];
			if (!cellTeam) {
				throw new Error(
					`Unknown opponent "${cellAbbrev}" for ${colAbbrev} on day ${day}`,
				);
			}
			if (colTeam.tid === cellTeam.tid) {
				throw new Error(`${colAbbrev} cannot play itself on day ${day}`);
			}
			if (teamsOnDay.has(cellTeam.tid)) {
				throw new Error(`${cellAbbrev} has multiple games on day ${day}`);
			}

			teamsOnDay.add(cellTeam.tid);

			dayGames.push({
				away: cellTeam,
				home: colTeam,
			});
		}
	}

	const newSchedule: Schedule = [...specials];
	for (const [day, dayGames] of gamesByDay) {
		for (const { away, home } of dayGames.values()) {
			newSchedule.push({
				type: "game",
				day,
				awayTid: away.tid,
				awayAbbrev: away.seasonAttrs.abbrev,
				homeTid: home.tid,
				homeAbbrev: home.seasonAttrs.abbrev,
			});
		}
	}

	return orderBy(newSchedule, ["day"]);
};

const exportScheduleCSV = (schedule: Schedule, teams: Team[]) => {
	const sortedTeams = orderBy(teams, (t) => t.seasonAttrs.abbrev);
	const abbrevs = sortedTeams.map((t) => t.seasonAttrs.abbrev);
	const abbrevToIdx = new Map(abbrevs.map((a, i) => [a, i]));

	const dayMap = new Map<number, Schedule[number][]>();
	for (const game of schedule) {
		if (game.type === "completed" || game.type === "placeholder") {
			continue;
		}
		dayMap.getOrInsertComputed(game.day, () => []).push(game);
	}

	const rows: string[][] = [["Day", ...abbrevs]];
	for (const day of orderBy([...dayMap.keys()], (d) => d)) {
		const row = Array<string>(abbrevs.length + 1).fill("");
		row[0] = String(day);
		for (const game of dayMap.get(day)!) {
			if (game.type === "allStarGame") {
				row[1] = ALL_STAR_GAME_LABEL;
			} else if (game.type === "tradeDeadline") {
				row[1] = TRADE_DEADLINE_LABEL;
			} else if (game.type === "game") {
				const homeIdx = abbrevToIdx.get(game.homeAbbrev);
				if (homeIdx !== undefined) {
					row[homeIdx + 1] = game.awayAbbrev;
				}
			}
		}
		rows.push(row);
	}

	downloadFile("schedule.csv", csvFormatRows(rows), "text/csv");
};

const reducer = (
	schedule: Schedule,
	action:
		| {
				type: "swapHomeAway" | "delete";
				game: ActualGame;
		  }
		| {
				type: "switchAwayTeam";
				away: Team;
				game: ActualGame;
		  }
		| {
				type: "switchHomeTeam";
				home: Team;
				game: ActualGame;
		  }
		| {
				type: "newGame";
				day: number;
				away: Team;
				home: Team;
		  }
		| {
				type: "deleteDay" | "addDay";
				day: number;
		  }
		| {
				type: "dragDay";
				oldDay: number;
				newDay: number;
		  }
		| {
				type: "swapDays";
				day1: number;
				day2: number;
		  }
		| {
				type: "resetSchedule";
				schedule: Schedule;
				dirty: boolean;
		  }
		| {
				type: "clearSchedule";
				maxDayAlreadyPlayed: number;
		  }
		| {
				type: "deleteSpecial";
				special: "allStarGame" | "tradeDeadline";
		  }
		| {
				type: "placeSpecial";
				special: "allStarGame" | "tradeDeadline";
				value: number;
		  },
): Schedule => {
	switch (action.type) {
		case "delete":
			return schedule.filter((row) => {
				return row !== action.game;
			});
		case "swapHomeAway":
			return schedule.map((row) => {
				if (row !== action.game) {
					return row;
				}

				return {
					...row,
					awayAbbrev: row.homeAbbrev,
					awayTid: row.homeTid,
					homeAbbrev: row.awayAbbrev,
					homeTid: row.awayTid,
				};
			});
		case "switchAwayTeam":
			return schedule.map((row) => {
				if (row !== action.game) {
					return row;
				}

				// New team means forceWin may no longer apply
				if (
					row.type === "game" &&
					(row.forceWin !== undefined || row.forceWinType !== undefined)
				) {
					delete row.forceWin;
					delete row.forceWinType;
				}

				return {
					...row,
					awayAbbrev: action.away.seasonAttrs.abbrev,
					awayTid: action.away.tid,
				};
			});
		case "switchHomeTeam":
			return schedule.map((row) => {
				if (row !== action.game) {
					return row;
				}

				// New team means forceWin may no longer apply
				if (
					row.type === "game" &&
					(row.forceWin !== undefined || row.forceWinType !== undefined)
				) {
					delete row.forceWin;
					delete row.forceWinType;
				}

				return {
					...row,
					homeAbbrev: action.home.seasonAttrs.abbrev,
					homeTid: action.home.tid,
				};
			});
		case "newGame":
			return orderBy(
				[
					...schedule,
					{
						type: "game",
						day: action.day,
						awayAbbrev: action.away.seasonAttrs.abbrev,
						awayTid: action.away.tid,
						homeAbbrev: action.home.seasonAttrs.abbrev,
						homeTid: action.home.tid,
					},
				],
				["day"],
			);
		case "deleteDay":
			return schedule
				.filter((game) => game.day !== action.day)
				.map((game) => {
					if (game.day < action.day) {
						return game;
					}

					return {
						...game,
						day: game.day - 1,
					};
				});
		case "addDay": {
			const placeholderGame = {
				type: "placeholder",
				day: action.day,
			} as const;
			return addGameOnDayByItself(schedule, placeholderGame);
		}
		case "dragDay":
			return orderBy(
				schedule.map((game) => {
					const minDay = Math.min(action.newDay, action.oldDay);
					const maxDay = Math.max(action.newDay, action.oldDay);

					if (game.day < minDay || game.day > maxDay) {
						return game;
					}

					if (game.day === action.oldDay) {
						return {
							...game,
							day: action.newDay,
						};
					}

					// By now it means we are in between newDay and oldDay
					if (action.newDay > action.oldDay) {
						return {
							...game,
							day: game.day - 1,
						};
					} else {
						return {
							...game,
							day: game.day + 1,
						};
					}
				}),
				["day"],
			);
		case "swapDays":
			return orderBy(
				schedule.map((game) => {
					if (game.day === action.day1) {
						return {
							...game,
							day: action.day2,
						};
					}

					if (game.day === action.day2) {
						return {
							...game,
							day: action.day1,
						};
					}

					return game;
				}),
				["day"],
			);
		case "resetSchedule":
			return action.schedule;
		case "clearSchedule": {
			// Can't clear completed games! Not sure why any is needed
			const newSchedule: Schedule = schedule.filter(
				(game) => game.type === "completed",
			);

			if (newSchedule.length === 0) {
				// If no games, need a placeholder so games can be added
				newSchedule.push({
					type: "placeholder",
					day: action.maxDayAlreadyPlayed + 1,
				});
			}

			return newSchedule;
		}
		case "deleteSpecial": {
			return deleteSpecial(schedule, action.special);
		}
		case "placeSpecial": {
			// Delete any existing ones
			const newSchedule = deleteSpecial(schedule, action.special);

			const index = Math.round(
				helpers.bound(action.value, 0, 1) * newSchedule.length,
			);

			const existingGame = newSchedule[index] ?? newSchedule[index - 1];
			const specialGame: Schedule[number] =
				action.special === "allStarGame"
					? {
							type: "allStarGame",
							day: 0,
							homeTid: -1,
							awayTid: -2,
						}
					: {
							type: "tradeDeadline",
							day: 0,
							homeTid: -3,
							awayTid: -3,
						};

			let newNewSchedule;
			if (!existingGame) {
				specialGame.day = (newSchedule.at(-1)?.day ?? 0) + 1;

				newNewSchedule = [...newSchedule, specialGame];
			} else if (existingGame.type === "placeholder") {
				specialGame.day = existingGame.day;

				newNewSchedule = newSchedule.map((game) => {
					if (game === existingGame) {
						return specialGame;
					}

					return game;
				});
			} else if (existingGame.type === "completed") {
				// Put after any completed days
				const lastCompletedGame = newSchedule.findLast(
					(game) => game.type === "completed",
				)!;
				specialGame.day = lastCompletedGame.day + 1;

				newNewSchedule = addGameOnDayByItself(newSchedule, specialGame);
			} else {
				specialGame.day = existingGame.day + 1;

				newNewSchedule = addGameOnDayByItself(newSchedule, specialGame);
			}

			notifyPlaceSpecial(action.special, specialGame.day);

			return newNewSchedule;
		}
	}
};

const ScheduleEditor = ({
	allStarGame,
	allStarGameAlreadyHappened,
	canRegenerateSchedule,
	maxDayAlreadyPlayed,
	schedule: scheduleProp,
	teams,
	tradeDeadline,
}: View<"scheduleEditor">) => {
	useTitleBar({ title: "Schedule Editor" });
	const { phase, userTid } = useLocal(["phase", "userTid"]);

	const [schedule, dispatchUnwrapped] = useReducer(reducer, scheduleProp);
	const [showSummaryStatistics, setShowSummaryStatistics] = useState(false);

	const { setDirty } = useBlocker();

	const dispatch: typeof dispatchUnwrapped = useCallback(
		(action) => {
			// Don't set dirty true when simming a game
			if (action.type !== "resetSchedule" || action.dirty) {
				setDirty(true);
			}
			dispatchUnwrapped(action);
		},
		[setDirty],
	);

	// Reset the saved schedule state when simming a game or something else that will affect this
	const isFirstRender = useRef(true);
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
		} else {
			dispatch({ type: "resetSchedule", schedule: scheduleProp, dirty: false });
		}
	}, [dispatch, scheduleProp]);

	const { godMode } = useLocal(["godMode"]);

	const [saving, setSaving] = useState(false);
	const [showRegenerateScheduleModal, setShowRegenerateScheduleModal] =
		useState(false);
	const [regenerated, setRegenerated] = useState(false);

	const handleFileUpload = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (!file) {
				return;
			}

			const reader = new FileReader();
			reader.readAsText(file);

			reader.onload = (e) => {
				try {
					const csvText = e.target?.result as string;
					const uploaded = validateAndParseScheduleCSV(
						csvText,
						teams,
						maxDayAlreadyPlayed,
					);
					const completed = schedule.filter((g) => g.type === "completed");
					dispatch({
						type: "resetSchedule",
						schedule: [...completed, ...uploaded],
						dirty: true,
					});
					showNotification({
						type: "success",
						text: `Successfully imported ${uploaded.length} games from CSV`,
					});
				} catch (error) {
					showNotification({
						type: "error",
						text: error.message,
					});
				}
			};

			reader.onerror = () => {
				showNotification({
					type: "error",
					text: "Failed to read CSV file",
				});
			};
		},
		[dispatch, maxDayAlreadyPlayed, schedule, teams],
	);

	if (phase !== PHASE.REGULAR_SEASON && phase !== PHASE.AFTER_TRADE_DEADLINE) {
		return <p>You can only edit the schedule during the regular season.</p>;
	}
	const scheduleByDay: ScheduleDay[] = [];
	for (const game of schedule) {
		let currentDay = scheduleByDay.at(-1);
		if (!currentDay) {
			if (game.type === "game" || game.type === "completed") {
				currentDay = {
					day: game.day,
					gamesByAwayTid: { [game.awayTid]: game },
					gamesByHomeTid: { [game.homeTid]: game },
					special: undefined,
				};
			} else {
				currentDay = {
					day: game.day,
					gamesByAwayTid: {},
					gamesByHomeTid: {},
					special: game.type === "placeholder" ? undefined : game.type,
				};
			}
			scheduleByDay.push(currentDay);
		} else if (currentDay.day <= game.day) {
			// Add blank days up to this game, if necessary
			let day = currentDay.day;
			let newDay = true;
			while (day < game.day) {
				day += 1;
				newDay = true;

				currentDay = {
					day,
					gamesByAwayTid: {},
					gamesByHomeTid: {},
					special: undefined,
				};
				scheduleByDay.push(currentDay);
			}

			if (game.type === "placeholder") {
				continue;
			}

			if (newDay) {
				if (game.type === "game" || game.type === "completed") {
					currentDay.gamesByAwayTid[game.awayTid] = game;
					currentDay.gamesByHomeTid[game.homeTid] = game;
				} else {
					currentDay.special = game.type;
				}
			} else {
				if (
					currentDay.gamesByHomeTid[game.homeTid] ||
					currentDay.gamesByHomeTid[game.awayTid] ||
					currentDay.gamesByAwayTid[game.homeTid] ||
					currentDay.gamesByAwayTid[game.awayTid]
				) {
					throw new Error(
						`Team ${game.homeTid} has two games on day ${game.day}`,
					);
				}
				if (game.type !== "game") {
					throw new Error(
						"All-Star Game and Trade Deadline must be on their own days",
					);
				}
				currentDay.gamesByAwayTid[game.awayTid] = game;
				currentDay.gamesByHomeTid[game.homeTid] = game;
			}
		} else {
			throw new Error("Schedule is not sorted by day");
		}
	}

	const teamsByTid = groupByUnique(teams, "tid");

	const cols = [
		{
			desc: helpers.upperCaseFirstLetter(TIME_BETWEEN_GAMES),
			title: helpers.upperCaseFirstLetter(TIME_BETWEEN_GAMES[0]!),
		},
		...getTeamCols(teams, userTid),
	];

	const rows = scheduleByDay.map((row) => {
		return {
			key: row.day,
			classNames: row.special ? "table-info" : undefined,
			data: [
				{
					// popperConfig renderOnMount is https://github.com/react-bootstrap/react-bootstrap/issues/6750#issuecomment-2609409983
					value: (
						<Dropdown>
							<Dropdown.Toggle
								bsPrefix="no-caret"
								style={{
									height,
								}}
								title="Actions"
								variant="btn-link border-0 py-0 px-2 w-100 d-flex align-items-center justify-content-end"
							>
								<span>
									{row.day}{" "}
									<span className="glyphicon glyphicon-option-vertical text-body-secondary" />
								</span>
							</Dropdown.Toggle>
							{godMode ? (
								<Dropdown.Menu
									popperConfig={{ strategy: "fixed" }}
									renderOnMount
								>
									{row.day > maxDayAlreadyPlayed ? (
										<Dropdown.Item
											onClick={() => {
												dispatch({
													type: "deleteDay",
													day: row.day,
												});
											}}
										>
											Delete {TIME_BETWEEN_GAMES} {row.day}
										</Dropdown.Item>
									) : null}
									{row.day > maxDayAlreadyPlayed ? (
										<Dropdown.Item
											onClick={() => {
												dispatch({
													type: "addDay",
													day: row.day,
												});
											}}
										>
											Add {TIME_BETWEEN_GAMES} above
										</Dropdown.Item>
									) : null}
									{row.day + 1 > maxDayAlreadyPlayed ? (
										<Dropdown.Item
											onClick={() => {
												dispatch({
													type: "addDay",
													day: row.day + 1,
												});
											}}
										>
											Add {TIME_BETWEEN_GAMES} below
										</Dropdown.Item>
									) : null}
								</Dropdown.Menu>
							) : null}
						</Dropdown>
					),
					searchValue: row.day,
					classNames: "p-0",
				},
				...(row.special
					? [
							{
								value:
									row.special === "allStarGame"
										? ALL_STAR_GAME_LABEL
										: TRADE_DEADLINE_LABEL,
								classNames: "text-start",
								colSpanToEnd: true,
							},
						]
					: teams.map((t) => {
							const gameAway = row.gamesByAwayTid[t.tid];
							const gameHome = row.gamesByHomeTid[t.tid];
							const game = gameHome ?? gameAway;

							// Highlight winner/loser of completed game
							let completedClassName;
							if (game?.type === "completed") {
								if (game.winnerTid === undefined) {
									completedClassName = "text-warning";
								} else if (game.winnerTid === t.tid) {
									completedClassName = "text-success";
								} else {
									completedClassName = "text-danger";
								}
							}

							return {
								value: (
									<FancySelect
										disabled={
											!godMode ||
											game?.type === "completed" ||
											row.day < maxDayAlreadyPlayed
										}
										value={
											gameHome
												? gameHome.awayTid
												: gameAway
													? gameAway.homeTid
													: "noGame"
										}
										className={clsx(
											"px-1",
											completedClassName,
											row.gamesByAwayTid[t.tid] &&
												completedClassName === undefined
												? "text-body-secondary"
												: undefined,
										)}
										onChange={(event) => {
											const value = event.target.value;
											if (value === "delete" || value === "swapHomeAway") {
												if (game) {
													dispatch({
														type: value,
														game,
													});
												}
											} else {
												const tid = Number.parseInt(value);
												const t2 = teamsByTid[tid];
												if (t2) {
													if (gameHome) {
														dispatch({
															type: "switchAwayTeam",
															away: t2,
															game: gameHome,
														});
													} else if (gameAway) {
														dispatch({
															type: "switchHomeTeam",
															home: t2,
															game: gameAway,
														});
													} else {
														dispatch({
															type: "newGame",
															away: t2,
															home: t,
															day: row.day,
														});
													}
												}
											}
										}}
										onMiddleClick={
											game
												? () => {
														dispatch({
															type: "swapHomeAway",
															game,
														});
													}
												: undefined
										}
										options={[
											...(game
												? [
														{
															key: "summary",
															value: `${game.awayAbbrev} @ ${game.homeAbbrev}`,
														},
														{ key: "delete", value: "Delete game" },
														{
															key: "swapHomeAway",
															value: "Swap home/away",
														},
													]
												: [
														{
															key: "summary",
															value: `${t.seasonAttrs.abbrev} vs...`,
														},
													]),
											...teams
												.filter((t2) => {
													// Don't show self
													if (t2.tid === t.tid) {
														return false;
													}

													// Keep team in list if it's the current opponent
													if (
														gameAway?.homeTid === t2.tid ||
														gameHome?.awayTid === t2.tid
													) {
														return true;
													}

													// Hide all teams with other games already
													return (
														!row.gamesByAwayTid[t2.tid] &&
														!row.gamesByHomeTid[t2.tid]
													);
												})
												.map((t2) => {
													return {
														key: t2.tid,
														value: t2.seasonAttrs.abbrev,
													};
												}),
										]}
									/>
								),
								searchValue: gameHome
									? gameHome.awayAbbrev
									: gameAway
										? `@${gameAway.homeAbbrev}`
										: undefined,
								classNames: "p-0",
							};
						})),
			],
		};
	});

	return (
		<div>
			<p>
				Home games are shown in normal text and away games are shown in{" "}
				<span className="text-body-secondary">faded text</span>.
			</p>
			{!godMode ? (
				<div>
					<span className="alert alert-warning d-inline-block mb-0">
						You can only edit the schedule in{" "}
						<a href={helpers.leagueUrl(["god_mode"])}>God Mode</a>.
					</span>
				</div>
			) : (
				<p className="mb-0">
					Click anywhere in the table to create/update/delete a game.
					{!window.mobile
						? " Middle click on a game to quickly swap the home and away teams."
						: null}
				</p>
			)}
			<DataTable
				className="text-center"
				clickable={false}
				cols={cols}
				defaultSort="disableSort"
				hideAllControls
				name="ScheduleEditor"
				rows={rows}
				sortableRows={
					godMode
						? {
								disableRow: (index) => {
									if (!scheduleByDay[index]) {
										return true;
									}
									return Object.values(
										scheduleByDay[index].gamesByAwayTid,
									).some((game) => game.type === "completed");
								},
								onChange: ({ oldIndex, newIndex }) => {
									dispatch({
										type: "dragDay",
										oldDay: oldIndex + 1,
										newDay: newIndex + 1,
									});
								},
								onSwap: (index1, index2) => {
									dispatch({
										type: "swapDays",
										day1: index1 + 1,
										day2: index2 + 1,
									});
								},
							}
						: undefined
				}
				stickyHeader
			/>

			{showSummaryStatistics ? (
				<>
					<h2 className="mt-5">Schedule Statistics</h2>
					<p className="mb-0">
						The numbers in this table show the total number of games for each
						team/category above (# home games) / (# away games).
					</p>
					<SummaryTable
						schedule={schedule}
						teams={teams}
						teamsByTid={teamsByTid}
						userTid={userTid}
					/>
				</>
			) : (
				<>
					<button
						className="btn btn-secondary"
						onClick={() => {
							setShowSummaryStatistics(true);
						}}
					>
						Show summary statistics
					</button>
				</>
			)}

			{godMode ? (
				<StickyBottomButtons>
					<Dropdown>
						<Dropdown.Toggle variant="secondary" disabled={saving}>
							Actions
						</Dropdown.Toggle>
						<Dropdown.Menu>
							<Dropdown.Item
								onClick={() => {
									let errorMessage;
									if (allStarGameAlreadyHappened) {
										errorMessage =
											"The All-Star Game already happened this season.";
									} else if (allStarGame === null) {
										errorMessage =
											"The All-Star Game is disabled. Go to Tools > League Settings if you want to change that.";
									} else if (allStarGame === -1) {
										errorMessage =
											"The All-Star Game happens during the playoffs. Go to Tools > League Settings if you want to change that.";
									}

									if (errorMessage) {
										dispatch({ type: "deleteSpecial", special: "allStarGame" });
										showNotification({
											type: "info",
											text: errorMessage,
										});
										return;
									}

									dispatch({
										type: "placeSpecial",
										special: "allStarGame",
										value: allStarGame!,
									});
								}}
							>
								Place All-Star Game in default position
							</Dropdown.Item>
							<Dropdown.Item
								onClick={() => {
									let errorMessage;
									if (phase >= PHASE.AFTER_TRADE_DEADLINE) {
										errorMessage =
											"The trade deadline already happened this season.";
									} else if (tradeDeadline === 1) {
										errorMessage =
											"The trade deadline is disabled. Go to Tools > League Settings if you want to change that.";
									}

									if (errorMessage) {
										dispatch({
											type: "deleteSpecial",
											special: "tradeDeadline",
										});
										showNotification({
											type: "info",
											text: errorMessage,
										});
										return;
									}

									dispatch({
										type: "placeSpecial",
										special: "tradeDeadline",
										value: tradeDeadline,
									});
								}}
							>
								Place Trade Deadline in default position
							</Dropdown.Item>
							<Dropdown.Item
								onClick={() => {
									if (!canRegenerateSchedule) {
										showNotification({
											type: "error",
											text: "You can only regenerate the schedule at the start of the regular season, when no games have been played.",
										});
										return;
									}

									setShowRegenerateScheduleModal(true);
								}}
							>
								Regenerate schedule
							</Dropdown.Item>
							<Dropdown.Item
								onClick={async () => {
									const proceed = await confirm(
										"Are you sure you want to delete the entire schedule?",
										{
											okText: "Clear schedule",
										},
									);
									if (proceed) {
										dispatch({
											type: "clearSchedule",
											maxDayAlreadyPlayed,
										});
									}
								}}
							>
								Clear schedule
							</Dropdown.Item>
							<Dropdown.Divider />
							<Dropdown.Item
								onClick={() => {
									exportScheduleCSV(schedule, teams);
								}}
							>
								Export schedule to CSV
							</Dropdown.Item>
							<Dropdown.Item
								// Like <ImportFileButton>
								as="div"
								style={{ position: "relative", overflow: "hidden" }}
							>
								Import schedule from CSV
								<input
									className="cursor-pointer"
									type="file"
									accept=".csv,text/csv"
									style={IMPORT_FILE_STYLE}
									onClick={resetFileInput}
									onChange={handleFileUpload}
								/>
							</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
					<button
						className="btn btn-primary ms-auto"
						disabled={saving}
						onClick={async () => {
							setSaving(true);

							try {
								await toWorker("main", "setScheduleFromEditor", {
									regenerated,
									schedule,
								});
							} catch (error) {
								showNotification({
									type: "error",
									text: `Error saving schedule: ${error.message}`,
								});
								throw error;
							}

							showNotification({
								type: "success",
								text: "Saved schedule",
							});

							setDirty(false);
							setSaving(false);
						}}
					>
						Save schedule
					</button>
				</StickyBottomButtons>
			) : null}
			<RegenerateScheduleModal
				show={showRegenerateScheduleModal}
				onCancel={() => {
					setShowRegenerateScheduleModal(false);
				}}
				onRegenerated={(schedule) => {
					setRegenerated(true);
					dispatch({
						type: "resetSchedule",
						schedule,
						dirty: true,
					});
					setShowRegenerateScheduleModal(false);
				}}
			/>
		</div>
	);
};

export default ScheduleEditor;
